import TeacherGradebookEntry from 'models/TeacherGradebookEntry';
import { useMemo, useState } from 'react';
import { ChartData } from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import StudentMetrics from 'models/analytics/StudentMetrics';

interface StudentMetricsChartProps {
  student?: TeacherGradebookEntry;
  metrics?: StudentMetrics;
}

const StudentMetricsChart: React.FC<StudentMetricsChartProps> = ({
  student,
  metrics,
}) => {
  const [viewLatestOnly, setViewLatestOnly] = useState(false);

  const widaEvaluations = useMemo(() => {
    return [...(metrics?.data[student?.username ?? ''].evaluations ?? [])]
      .filter((entry) => entry.evaluation.type === 'wida')
      .sort(
        (a, b) =>
          new Date(a.submission.date_completed).getTime() -
          new Date(b.submission.date_completed).getTime(),
      );
  }, [metrics, student?.username]);

  const classAverages = useMemo(
    () =>
      metrics?.averages ?? {
        wida_reading: 0,
        wida_writing: 0,
        wida_listening: 0,
        wida_speaking: 0,
      },
    [metrics?.averages],
  );

  const lineChartData = useMemo<ChartData<'line'>>(() => {
    const labels = widaEvaluations.map((m) =>
      new Date(m.submission.date_completed).toLocaleDateString(),
    );

    const extractData = (
      key: keyof (typeof widaEvaluations)[0]['submission'],
    ) =>
      widaEvaluations.map((m) =>
        m.submission[key] ? parseFloat(m.submission[key] as string) : null,
      );

    const classAverageArray = (avg: number) =>
      new Array(labels.length).fill(avg);

    return {
      labels,
      datasets: [
        {
          label: 'Reading',
          data: extractData('wida_reading'),
          borderColor: '#5A007D',
          backgroundColor: 'rgba(90, 0, 125, 0.1)',
          tension: 0.3,
        },
        {
          label: 'Writing',
          data: extractData('wida_writing'),
          borderColor: '#007D5A',
          backgroundColor: 'rgba(0, 125, 90, 0.1)',
          tension: 0.3,
        },
        {
          label: 'Listening',
          data: extractData('wida_listening'),
          borderColor: '#007DBB',
          backgroundColor: 'rgba(0, 125, 187, 0.1)',
          tension: 0.3,
        },
        {
          label: 'Speaking',
          data: extractData('wida_speaking'),
          borderColor: '#BB7D00',
          backgroundColor: 'rgba(187, 125, 0, 0.1)',
          tension: 0.3,
        },
        {
          label: 'Class Avg (Reading)',
          data: classAverageArray(classAverages.wida_reading),
          borderDash: [5, 5],
          borderColor: '#5A007D',
          backgroundColor: 'transparent',
        },
        {
          label: 'Class Avg (Writing)',
          data: classAverageArray(classAverages.wida_writing),
          borderDash: [5, 5],
          borderColor: '#007D5A',
          backgroundColor: 'transparent',
        },
        {
          label: 'Class Avg (Listening)',
          data: classAverageArray(classAverages.wida_listening),
          borderDash: [5, 5],
          borderColor: '#007DBB',
          backgroundColor: 'transparent',
        },
        {
          label: 'Class Avg (Speaking)',
          data: classAverageArray(classAverages.wida_speaking),
          borderDash: [5, 5],
          borderColor: '#BB7D00',
          backgroundColor: 'transparent',
        },
      ],
    };
  }, [widaEvaluations, classAverages]);

  const barChartData = useMemo<ChartData<'bar'>>(() => {
    const latest = widaEvaluations[widaEvaluations.length - 1]?.submission;
    if (!latest) return { labels: [], datasets: [] };

    return {
      labels: ['Reading', 'Writing', 'Listening', 'Speaking'],
      datasets: [
        {
          label: student?.displayName() || 'Latest Scores',
          data: [
            parseFloat(latest.wida_reading || '0'),
            parseFloat(latest.wida_writing || '0'),
            parseFloat(latest.wida_listening || '0'),
            parseFloat(latest.wida_speaking || '0'),
          ],
          backgroundColor: ['#5A007D', '#007D5A', '#007DBB', '#BB7D00'],
          borderWidth: 1,
        },
        {
          label: 'Class Average',
          data: [
            classAverages.wida_reading,
            classAverages.wida_writing,
            classAverages.wida_listening,
            classAverages.wida_speaking,
          ],
          backgroundColor: 'rgba(0,0,0,0.1)',
          borderColor: 'black',
          borderWidth: 1,
        },
      ],
    };
  }, [widaEvaluations, student, classAverages]);

  const sharedOptions = {
    maintainAspectRatio: true,
    responsive: true,
    scales: {
      y: {
        beginAtZero: true,
        max: 6,
        title: { display: true, text: 'WIDA Score' },
      },
    },
    plugins: {
      legend: {
        position: 'top' as const,
      },
    },
  };

  return (
    <>
      {viewLatestOnly ? (
        <Bar data={barChartData} options={sharedOptions} />
      ) : (
        <Line data={lineChartData} options={sharedOptions} />
      )}
      <label className="label-medium">
        <input
          type="checkbox"
          checked={viewLatestOnly}
          onChange={() => setViewLatestOnly((prev) => !prev)}
          style={{ marginRight: '0.5rem' }}
        />
        View Current Metrics
      </label>
    </>
  );
};

export default StudentMetricsChart;
